import type { CrmPage } from './crmBridge'
import { hasFanslyPage } from '../fansly/registry'

/**
 * The CRM page rail the console draws on every account CRM route (ADR 0023).
 * Which pages appear follows the CRM's own page permissions (its
 * GET /v1/permissions snapshot, the same data its page-access.js reads); the
 * CRM still enforces them.
 */

export interface CrmPermissions {
  super_admin?: boolean
  account_owner?: boolean
  features?: Record<string, { can_read?: boolean; can_write?: boolean } | undefined>
}

export interface RailPage {
  page: CrmPage
  /** Opens the page on one of its views: the page's own URL query (without "?"), e.g. `view=followers`. */
  query?: string
  /** A symbol id from the CRM sprite (see crmRailIcons). */
  icon: string
  labelKey: string
  /** Other pages it needs to read (Fansly's subscribers come through a Chats route). */
  needs?: readonly CrmPage[]
  /**
   * Fansly style only: where fansly.com keeps it. `bar`: an icon on the top
   * bar; otherwise the account (avatar) menu section it sits in.
   */
  place?: 'bar' | FanslyMenuSection
}

/** The account (avatar) menu's sections on fansly.com, in its order. */
export const FANSLY_MENU_SECTIONS = ['account', 'creator', 'settings'] as const
export type FanslyMenuSection = (typeof FANSLY_MENU_SECTIONS)[number]

/**
 * The menu a Fansly account shows (Settings → Styling): the OnlyFans rail, or
 * Fansly's own creator menu. OnlyFans accounts always have the OnlyFans one.
 */
export const MENU_STYLES = ['onlyfans', 'fansly'] as const
export type MenuStyle = (typeof MENU_STYLES)[number]

/** The CRM Sidebar's pages, in its order (crm/src/public/index.html). */
const RAIL: readonly RailPage[] = [
  { page: 'home', icon: 'icon-home', labelKey: 'crmRail.home' },
  { page: 'notifications', icon: 'icon-notifications', labelKey: 'crmRail.notifications' },
  { page: 'chats', icon: 'icon-messages', labelKey: 'crmRail.chats' },
  { page: 'collections', icon: 'icon-collections-lg', labelKey: 'crmRail.collections' },
  { page: 'vault', icon: 'icon-vault-lg', labelKey: 'crmRail.vault' },
  { page: 'queue', icon: 'icon-calendar', labelKey: 'crmRail.queue' },
  { page: 'statements', icon: 'icon-statements-temp-lg', labelKey: 'crmRail.statements' },
  { page: 'statistics', icon: 'icon-statements-lg', labelKey: 'crmRail.statistics' },
  { page: 'profile', icon: 'icon-profile-round', labelKey: 'crmRail.profile' },
  { page: 'more', icon: 'icon-more', labelKey: 'crmRail.more' },
]

/**
 * Fansly style: fansly.com's own navigation (its app-nav-bar-top). No side
 * menu: the account's name on the left goes Home (Fansly's logo); on the
 * right Messages, Notifications and Earnings (Fansly's wallet balance) sit on
 * the bar, and the account picture opens the account menu: Profile and Media
 * Vault, the Creator section (Plans/Promos, statistics, Tracking Links,
 * Scheduled posts, then the Creator Dashboard's Subscribers, Followers,
 * Lists, Management, Streaming), and the settings. Order is Fansly's.
 */
const FANSLY_RAIL: readonly RailPage[] = [
  { page: 'home', icon: 'fansly-home', labelKey: 'crmRail.home', place: 'bar' },
  { page: 'chats', icon: 'fansly-mail', labelKey: 'crmRail.chats', place: 'bar' },
  { page: 'notifications', icon: 'fansly-bell', labelKey: 'crmRail.notifications', place: 'bar' },
  { page: 'statements', icon: 'fansly-wallet', labelKey: 'crmRail.fansly.earnings', place: 'bar' },
  { page: 'profile', icon: 'fansly-profile', labelKey: 'crmRail.fansly.profile', place: 'account' },
  { page: 'vault', icon: 'fansly-vault', labelKey: 'crmRail.fansly.vault', place: 'account' },
  { page: 'plans', icon: 'fansly-card', labelKey: 'crmRail.fansly.plans', place: 'creator' },
  { page: 'statistics', icon: 'fansly-stats', labelKey: 'crmRail.statistics', place: 'creator' },
  { page: 'tracking', icon: 'fansly-link', labelKey: 'crmRail.fansly.tracking', place: 'creator' },
  { page: 'queue', icon: 'fansly-calendar', labelKey: 'crmRail.fansly.scheduled', place: 'creator' },
  { page: 'collections', query: 'view=subscribers', icon: 'fansly-users', labelKey: 'crmRail.fansly.subscribers', needs: ['chats'], place: 'creator' },
  { page: 'collections', query: 'view=followers', icon: 'fansly-user-plus', labelKey: 'crmRail.fansly.followers', place: 'creator' },
  { page: 'collections', query: 'view=lists', icon: 'fansly-list', labelKey: 'crmRail.fansly.lists', place: 'creator' },
  { page: 'management', icon: 'fansly-shield', labelKey: 'crmRail.fansly.management', place: 'creator' },
  { page: 'streaming', icon: 'fansly-video', labelKey: 'crmRail.fansly.streaming', place: 'creator' },
  { page: 'messaging', icon: 'fansly-message', labelKey: 'crmRail.fansly.messaging', place: 'settings' },
  { page: 'settings', icon: 'fansly-settings', labelKey: 'crmRail.fansly.settings', place: 'settings' },
]

/** Pages the console renders itself; the rest stay in the CRM frame. */
const REACT_PAGES: ReadonlySet<string> = new Set([
  'vault',
  'collections',
  'queue',
  'profile',
  'home',
  'notifications',
  'statements',
  'statistics',
  'more',
  'banking',
  // Reached from More (OnlyFans' sidebar menu).
  'settings',
  'streaming',
  'referrals',
  'discover',
  'releaseforms',
  'cards',
])

/**
 * Fansly pages reached from More borrow the feature of the CRM page that
 * guards their routes (crm/.../fansly-crm/lib/contract.js).
 */
const FEATURE_OF: Partial<Record<CrmPage, string>> = {
  plans: 'page.profile',
  tracking: 'page.statistics',
  messaging: 'page.chats',
  management: 'page.profile',
  streaming: 'page.home',
  settings: 'page.profile',
  referrals: 'page.statistics',
  discover: 'page.home',
  releaseforms: 'page.home',
}

/** Pages only the account owner (or a super admin) may open: Your cards. */
const OWNER_ONLY: ReadonlySet<CrmPage> = new Set(['cards'])

/** Account owner or super admin: Settings → Account security, Your cards. */
export function isOwnerOf(perms: CrmPermissions | null): boolean {
  return Boolean(perms && (perms.super_admin || perms.account_owner))
}

function featureOf(page: CrmPage): string {
  return FEATURE_OF[page] ?? `page.${page}`
}

export function canRead(perms: CrmPermissions | null, page: CrmPage): boolean {
  if (!perms) return false
  if (perms.super_admin || perms.account_owner) return true
  if (OWNER_ONLY.has(page)) return false
  return perms.features?.[featureOf(page)]?.can_read === true
}

export function canWrite(perms: CrmPermissions | null, page: CrmPage): boolean {
  if (!perms) return false
  if (perms.super_admin || perms.account_owner) return true
  if (OWNER_ONLY.has(page)) return false
  return perms.features?.[featureOf(page)]?.can_write === true
}

/** Pages that live under More rather than on the rail. */
const UNDER_MORE: ReadonlySet<CrmPage> = new Set([
  'banking',
  'plans',
  'tracking',
  'messaging',
  'management',
  'streaming',
  'settings',
  'referrals',
  'discover',
  'releaseforms',
  'cards',
])

/** The rail entry a page sits under: Banking and the Fansly tools are reached from More (Fansly style lists every page itself). */
export function railPageOf(page: CrmPage, style: MenuStyle = 'onlyfans'): CrmPage {
  return style === 'onlyfans' && UNDER_MORE.has(page) ? 'more' : page
}

export function railPages(perms: CrmPermissions | null, style: MenuStyle = 'onlyfans'): RailPage[] {
  return (style === 'fansly' ? FANSLY_RAIL : RAIL).filter((p) => canRead(perms, p.page) && (p.needs ?? []).every((n) => canRead(perms, n)))
}

/** Where a rail entry goes, under the account's CRM base path. */
export function railHref(base: string, p: RailPage): string {
  return `${base}/${p.page}${p.query ? `?${p.query}` : ''}`
}

/**
 * Which of the shown entries is lit for the page on screen and its URL query
 * (`search`, with or without "?"). Several entries can open one page on
 * different views (Fansly's Subscribers, Followers and Lists): the one whose
 * view the URL names, else the first of them. Any other Collections view
 * (one list, Fansly's own lists) lights Lists.
 */
export function railEntryOn(pages: readonly RailPage[], page: CrmPage, search: string, style: MenuStyle = 'onlyfans'): RailPage | null {
  const shown = railPageOf(page, style)
  const same = pages.filter((p) => p.page === shown)
  if (same.length < 2) return same[0] ?? null
  const asked = new URLSearchParams(search).get('view') ?? ''
  const view = /^(list|sys):/.test(asked) ? 'lists' : asked
  return same.find((p) => new URLSearchParams(p.query ?? '').get('view') === view) ?? same[0] ?? null
}

/** A stable React key for a rail entry (a page can be on the rail more than once). */
export function railKey(p: RailPage): string {
  return p.query ? `${p.page}?${p.query}` : p.page
}

/** What decides React pages beyond the page itself. */
export interface ReactPageGate {
  /** The React Chats page is switched on for this person (ADR 0024 stages). */
  reactChats?: boolean
  /** Fansly accounts get the console's React pages instead of the embedded Fansly workspace (ADR 0036). */
  fanslyReact?: boolean
}

/** Pages that exist only for Fansly accounts: an OnlyFans account sends them to More. (Settings and Streaming have OnlyFans versions.) */
const FANSLY_ONLY: ReadonlySet<string> = new Set(['plans', 'tracking', 'messaging', 'management'])

/** OnlyFans pages under More with no Fansly version: a Fansly account sends them to More. */
const ONLYFANS_ONLY: ReadonlySet<string> = new Set(['referrals', 'discover', 'releaseforms', 'cards'])

export function isOnlyFansOnlyPage(page: string): boolean {
  return ONLYFANS_ONLY.has(page)
}

export function isFanslyOnlyPage(page: string): boolean {
  return FANSLY_ONLY.has(page)
}

/**
 * Whether the console renders this page itself. A missing platform is
 * OnlyFans (ADR 0019). Chats is React only while its stage gate is on.
 * A Fansly account renders React pages while `fanslyReact` is on (the frame,
 * with its embedded Fansly workspace, otherwise); a Fansly page not built yet
 * still shows the frame.
 */
export function rendersInReact(page: string, platform?: string | null, gate: ReactPageGate = {}): boolean {
  if (platform === 'fansly') return gate.fanslyReact === true && hasFanslyPage(page)
  return REACT_PAGES.has(page) || (page === 'chats' && gate.reactChats === true)
}
